import { createContext, useContext, useEffect, useState } from 'react';

import { AuthClient, IPasswordLoginParams, IAuthorizationCodeLoginParams } from 'shared/utils/AuthClient';
import { AuthTenantID, ITokenData, ITokenDataSystem, ITokenDataTenant } from 'shared/modules/auth/models';

interface IUseAuthProviderProps {
    tenantId: AuthTenantID;
}

interface IUseAuthProviderValue {
    tenantId: AuthTenantID;
    tokenData: ITokenData | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    getAccessTokenSilently: () => Promise<string>;
    signOut: () => Promise<void>;
    authenticateWithCredentials: (loginParams: IPasswordLoginParams) => Promise<string>;
    authenticateWithCode: (authorizationCodeParams: IAuthorizationCodeLoginParams) => Promise<string>;
}

interface IUseGuaranteedAuthValue extends IUseAuthProviderValue {
    tenantId: number;
    tokenData: ITokenDataTenant;
}

interface IUseGuaranteedSystemAuthValue extends IUseAuthProviderValue {
    tenantId: null;
    tokenData: ITokenDataSystem;
}

interface IUseAuthProviderValueSystem extends IUseAuthProviderValue {
    tenantId: null;
    tokenData: ITokenDataSystem | null;
}

interface IUseGuaranteedAuthValueTenant extends IUseGuaranteedAuthValue {
    tenantId: number;
    tokenData: ITokenDataTenant;
}

const stub = (): never => {
    throw new Error('You forgot to wrap your component in <AuthProvider>.');
};

const initialAuthContext: IUseAuthProviderValue = {
    isLoading: true,
    isAuthenticated: false,
    tenantId: null,
    tokenData: null,
    getAccessTokenSilently: stub,
    signOut: stub,
    authenticateWithCredentials: stub,
    authenticateWithCode: stub,
};

interface IUseAuthProviderState {
    tokenData: ITokenData | null;
    isLoading: boolean;
    isAuthenticated: boolean;
}

export const authContext = createContext<IUseAuthProviderValue>(initialAuthContext);

export const useSystemAuth = (): IUseAuthProviderValueSystem => {
    const result = useContext(authContext);
    if (result.isAuthenticated && result.tenantId) {
        throw new Error('Found tenant auth');
    }
    return {
        ...result,
        tokenData: result.tokenData as ITokenDataSystem,
        tenantId: null,
    };
};

export const useGuaranteedSystemAuth = (): IUseGuaranteedSystemAuthValue => {
    const result = useSystemAuth();
    console.log(result);
    if (!result.isAuthenticated || result.tenantId || !result.tokenData) {
        throw new Error('Not signed in');
    }
    return {
        ...result,
        tokenData: result.tokenData,
        tenantId: null,
    };
};

export const useAuth = () => {
    return useContext(authContext);
};

export const useGuaranteedAuth = (): IUseGuaranteedAuthValueTenant => {
    const {tokenData, tenantId, ...rest} = useContext(authContext);
    if (!tokenData) {
        throw new Error('not authenticated');
    }
    if (!tenantId) {
        throw new Error('not authenticated');
    }
    return {
        tokenData: tokenData as ITokenDataTenant,
        tenantId,
        ...rest,
    };
};

const getUserFromClient = (client: AuthClient): ITokenData | null => {
    if (client.userId && client.scopes) {
        return {
            id: client.userId,
            scopes: client.scopes,
        };
    }
    return null;
};

// Provider hook that creates auth object and handles state
export function useAuthProvider({tenantId}: IUseAuthProviderProps): IUseAuthProviderValue {
    const [client] = useState<AuthClient>(
        new AuthClient({
            tenantId,
        }),
    );

    const [authState, setAuthState] = useState<IUseAuthProviderState>({
        tokenData: getUserFromClient(client),
        isAuthenticated: client.isAuthenticated || false,
        isLoading: !client.isAuthenticated,
    });

    // initial check to see if the user is authenticated
    useEffect(() => {
        if (authState.isAuthenticated) {
            return;
        }
        (async (): Promise<void> => {
            try {
                await client.getAccessTokenSilently();
                setAuthState({
                    tokenData: getUserFromClient(client),
                    isLoading: false,
                    isAuthenticated: true,
                });
            } catch (error) {
                setAuthState({
                    tokenData: null,
                    isLoading: false,
                    isAuthenticated: false,
                });
            }
        })();
        // ignore missing dependencies to ensure it only runs once:
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getAccessTokenSilently = async (): Promise<string> => {
        try {
            const accessToken = await client.getAccessTokenSilently();
            setAuthState({
                tokenData: getUserFromClient(client),
                isLoading: false,
                isAuthenticated: true,
            });
            return accessToken;
        } catch (e) {
            setAuthState({
                tokenData: null,
                isLoading: false,
                isAuthenticated: false,
            });
            throw e;
        }
    };

    const signOut = async () => {
        const isAuthenticated = client.isAuthenticated;
        try {
            await client.signOut();
            setAuthState({
                tokenData: null,
                isLoading: false,
                isAuthenticated: false,
            });
        } catch (error) {
            setAuthState({
                tokenData: null,
                isLoading: false,
                isAuthenticated: false,
            });
        } finally {
            if (isAuthenticated) {
                window.location.reload();
            }
        }
    };

    const authenticateWithCredentials = async (loginParams: IPasswordLoginParams): Promise<string> => {
        try {
            const accessToken = await client.authenticateWithCredentials(loginParams);
            setAuthState({
                tokenData: getUserFromClient(client),
                isLoading: false,
                isAuthenticated: true,
            });
            return accessToken;
        } catch (error) {
            setAuthState({
                tokenData: null,
                isLoading: false,
                isAuthenticated: false,
            });
            throw error;
        }
    };

    const authenticateWithCode = async (loginParams: IAuthorizationCodeLoginParams): Promise<string> => {
        try {
            const accessToken = await client.authenticateWithCode(loginParams);
            setAuthState({
                tokenData: getUserFromClient(client),
                isLoading: false,
                isAuthenticated: true,
            });
            return accessToken;
        } catch (error) {
            setAuthState({
                tokenData: null,
                isLoading: false,
                isAuthenticated: false,
            });
            throw error;
        }
    };

    // Return the user object and auth methods
    return {
        tenantId,
        tokenData: authState.tokenData,
        isLoading: authState.isLoading,
        isAuthenticated: authState.isAuthenticated,
        getAccessTokenSilently,
        signOut,
        authenticateWithCredentials,
        authenticateWithCode,
    };
}
